/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
    NgModule,
    Directive,
    EventEmitter,
    TemplateRef,
    ViewContainerRef,
    Optional,
    Input,
    OnDestroy,
    Output,
    ElementRef,
    Renderer2,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import {Overlay, OVERLAY_PROVIDERS} from './overlay';
import {OverlayRef} from './overlay-ref';
import {TemplatePortal} from '../portal/portal';
import {OverlayState} from './overlay-state';
import {
    ConnectionPositionPair,
    ConnectedOverlayPositionChange
} from './position/connected-position';
import {PortalModule} from '../portal/portal-directives';
import {ConnectedPositionStrategy} from './position/connected-position-strategy';
import {Dir, LayoutDirection} from '../rtl/dir';
import {Scrollable} from './scroll/scrollable';
import {ScrollStrategy} from './scroll/scroll-strategy';
import {coerceBooleanProperty} from '../coercion/boolean-property';
import {ESCAPE} from '../keyboard/keycodes';
import {Subscription} from 'rxjs/Subscription';
import {ScrollDispatchModule} from './scroll/index';
/**
 * Default set of positions for the overlay. Follows the behavior of a dropdown.
 */
let defaultPositionList = [
  new ConnectionPositionPair(
      {originX: 'start', originY: 'bottom'},
      {overlayX: 'start', overlayY: 'top'}),
  new ConnectionPositionPair(
      {originX: 'start', originY: 'top'},
      {overlayX: 'start', overlayY: 'bottom'}),
];
/**
 * Directive applied to an element to make it usable as an origin for an Overlay using a
 * ConnectedPositionStrategy.
 */
export class OverlayOrigin {
/**
 * @param {?} elementRef
 */
constructor(public elementRef: ElementRef) { }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdk-overlay-origin], [overlay-origin], [cdkOverlayOrigin]',
  exportAs: 'cdkOverlayOrigin',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
}

function OverlayOrigin_tsickle_Closure_declarations() {
/** @type {?} */
OverlayOrigin.decorators;
/**
 * @nocollapse
 * @type {?}
 */
OverlayOrigin.ctorParameters;
/** @type {?} */
OverlayOrigin.prototype.elementRef;
}

/**
 * Directive to facilitate declarative creation of an Overlay using a ConnectedPositionStrategy.
 */
export class ConnectedOverlayDirective implements OnDestroy, OnChanges {
private _overlayRef: OverlayRef;
private _templatePortal: TemplatePortal;
private _hasBackdrop = false;
private _backdropSubscription: Subscription;
private _positionSubscription: Subscription;
private _offsetX: number = 0;
private _offsetY: number = 0;
private _position: ConnectedPositionStrategy;
private _escapeListener: Function;
/**
 * Origin for the connected overlay.
 */
origin: OverlayOrigin;
/**
 * Registered connected position pairs.
 */
positions: ConnectionPositionPair[];
/**
 * The offset in pixels for the overlay connection point on the x-axis
 * @return {?}
 */
get offsetX(): number {
    return this._offsetX;
  }
/**
 * @param {?} offsetX
 * @return {?}
 */
set offsetX(offsetX: number) {
    this._offsetX = offsetX;
    if (this._position) {
      this._position.withOffsetX(offsetX);
    }
  }
/**
 * The offset in pixels for the overlay connection point on the y-axis
 * @return {?}
 */
get offsetY() {
    return this._offsetY;
  }
/**
 * @param {?} offsetY
 * @return {?}
 */
set offsetY(offsetY: number) {
    this._offsetY = offsetY;
    if (this._position) {
      this._position.withOffsetY(offsetY);
    }
  }
/**
 * The width of the overlay panel.
 */
width: number | string;
/**
 * The height of the overlay panel.
 */
height: number | string;
/**
 * The min width of the overlay panel.
 */
minWidth: number | string;
/**
 * The min height of the overlay panel.
 */
minHeight: number | string;
/**
 * The custom class to be set on the backdrop element.
 */
backdropClass: string;
/**
 * Strategy to be used when handling scroll events while the overlay is open.
 */
scrollStrategy: ScrollStrategy = this._overlay.scrollStrategies.reposition();
/**
 * Whether the overlay is open.
 */
open: boolean = false;
/**
 * Whether or not the overlay should attach a backdrop.
 * @return {?}
 */
get hasBackdrop() {
    return this._hasBackdrop;
  }
/**
 * @param {?} value
 * @return {?}
 */
set hasBackdrop(value: any) {
    this._hasBackdrop = coerceBooleanProperty(value);
  }
/**
 * Event emitted when the backdrop is clicked.
 */
backdropClick = new EventEmitter<void>();
/**
 * Event emitted when the position has changed.
 */
positionChange = new EventEmitter<ConnectedOverlayPositionChange>();
/**
 * Event emitted when the overlay has been attached.
 */
attach = new EventEmitter<void>();
/**
 * Event emitted when the overlay has been detached.
 */
detach = new EventEmitter<void>();
/**
 * @param {?} _overlay
 * @param {?} _renderer
 * @param {?} templateRef
 * @param {?} viewContainerRef
 * @param {?} _dir
 */
constructor(
private _overlay: Overlay,
private _renderer: Renderer2,
      templateRef: TemplateRef<any>,
      viewContainerRef: ViewContainerRef,
private _dir: Dir) {
    this._templatePortal = new TemplatePortal(templateRef, viewContainerRef);
  }
/**
 * The associated overlay reference.
 * @return {?}
 */
get overlayRef(): OverlayRef {
    return this._overlayRef;
  }
/**
 * The element's layout direction.
 * @return {?}
 */
get dir(): LayoutDirection {
    return this._dir ? this._dir.value : 'ltr';
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._destroyOverlay();
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    if (changes['open']) {
      this.open ? this._attachOverlay() : this._detachOverlay();
    }
  }
/**
 * Creates an overlay
 * @return {?}
 */
private _createOverlay() {
    if (!this.positions || !this.positions.length) {
      this.positions = defaultPositionList;
    }

    this._overlayRef = this._overlay.create(this._buildConfig());
  }
/**
 * Builds the overlay config based on the directive's inputs
 * @return {?}
 */
private _buildConfig(): OverlayState {
    let /** @type {?} */ overlayConfig = new OverlayState();

    if (this.width || this.width === 0) {
      overlayConfig.width = this.width;
    }

    if (this.height || this.height === 0) {
      overlayConfig.height = this.height;
    }

    if (this.minWidth || this.minWidth === 0) {
      overlayConfig.minWidth = this.minWidth;
    }

    if (this.minHeight || this.minHeight === 0) {
      overlayConfig.minHeight = this.minHeight;
    }

    overlayConfig.hasBackdrop = this.hasBackdrop;

    if (this.backdropClass) {
      overlayConfig.backdropClass = this.backdropClass;
    }

    this._position = /** @type {?} */(( this._createPositionStrategy() as ConnectedPositionStrategy));
    overlayConfig.positionStrategy = this._position;
    overlayConfig.scrollStrategy = this.scrollStrategy;

    return overlayConfig;
  }
/**
 * Returns the position strategy of the overlay to be set on the overlay config
 * @return {?}
 */
private _createPositionStrategy(): ConnectedPositionStrategy {
    const /** @type {?} */ pos = this.positions[0];
    const /** @type {?} */ originPoint = {originX: pos.originX, originY: pos.originY};
    const /** @type {?} */ overlayPoint = {overlayX: pos.overlayX, overlayY: pos.overlayY};

    const /** @type {?} */ strategy = this._overlay.position()
      .connectedTo(this.origin.elementRef, originPoint, overlayPoint)
      .withOffsetX(this.offsetX)
      .withOffsetY(this.offsetY);

    this._handlePositionChanges(strategy);

    return strategy;
  }
/**
 * @param {?} strategy
 * @return {?}
 */
private _handlePositionChanges(strategy: ConnectedPositionStrategy): void {
    for (let /** @type {?} */ i = 1; i < this.positions.length; i++) {
      strategy.withFallbackPosition(
          {originX: this.positions[i].originX, originY: this.positions[i].originY},
          {overlayX: this.positions[i].overlayX, overlayY: this.positions[i].overlayY}
      );
    }

    this._positionSubscription =
        strategy.onPositionChange.subscribe(pos => this.positionChange.emit(pos));
  }
/**
 * Attaches the overlay and subscribes to backdrop clicks if backdrop exists
 * @return {?}
 */
private _attachOverlay() {
    if (!this._overlayRef) {
      this._createOverlay();
    }

    this._position.withDirection(this.dir);
    this._overlayRef.getState().direction = this.dir;
    this._initEscapeListener();

    if (!this._overlayRef.hasAttached()) {
      this._overlayRef.attach(this._templatePortal);
      this.attach.emit();
    }

    if (this.hasBackdrop) {
      this._backdropSubscription = this._overlayRef.backdropClick().subscribe(() => {
        this.backdropClick.emit();
      });
    }
  }
/**
 * Detaches the overlay and unsubscribes to backdrop clicks if backdrop exists
 * @return {?}
 */
private _detachOverlay() {
    if (this._overlayRef) {
      this._overlayRef.detach();
      this.detach.emit();
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
      this._backdropSubscription = null;
    }

    if (this._escapeListener) {
      this._escapeListener();
    }
  }
/**
 * Destroys the overlay created by this directive.
 * @return {?}
 */
private _destroyOverlay() {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }

    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
    }

    if (this._positionSubscription) {
      this._positionSubscription.unsubscribe();
    }

    if (this._escapeListener) {
      this._escapeListener();
    }
  }
/**
 * Sets the event listener that closes the overlay when pressing Escape.
 * @return {?}
 */
private _initEscapeListener() {
    this._escapeListener = this._renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if (event.keyCode === ESCAPE) {
        this._detachOverlay();
      }
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]',
  exportAs: 'cdkConnectedOverlay'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Overlay, },
{type: Renderer2, },
{type: TemplateRef, },
{type: ViewContainerRef, },
{type: Dir, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'origin': [{ type: Input },],
'positions': [{ type: Input },],
'offsetX': [{ type: Input },],
'offsetY': [{ type: Input },],
'width': [{ type: Input },],
'height': [{ type: Input },],
'minWidth': [{ type: Input },],
'minHeight': [{ type: Input },],
'backdropClass': [{ type: Input },],
'scrollStrategy': [{ type: Input },],
'open': [{ type: Input },],
'hasBackdrop': [{ type: Input },],
'backdropClick': [{ type: Output },],
'positionChange': [{ type: Output },],
'attach': [{ type: Output },],
'detach': [{ type: Output },],
};
}

function ConnectedOverlayDirective_tsickle_Closure_declarations() {
/** @type {?} */
ConnectedOverlayDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ConnectedOverlayDirective.ctorParameters;
/** @type {?} */
ConnectedOverlayDirective.propDecorators;
/** @type {?} */
ConnectedOverlayDirective.prototype._overlayRef;
/** @type {?} */
ConnectedOverlayDirective.prototype._templatePortal;
/** @type {?} */
ConnectedOverlayDirective.prototype._hasBackdrop;
/** @type {?} */
ConnectedOverlayDirective.prototype._backdropSubscription;
/** @type {?} */
ConnectedOverlayDirective.prototype._positionSubscription;
/** @type {?} */
ConnectedOverlayDirective.prototype._offsetX;
/** @type {?} */
ConnectedOverlayDirective.prototype._offsetY;
/** @type {?} */
ConnectedOverlayDirective.prototype._position;
/** @type {?} */
ConnectedOverlayDirective.prototype._escapeListener;
/**
 * Origin for the connected overlay.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.origin;
/**
 * Registered connected position pairs.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.positions;
/**
 * The width of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.width;
/**
 * The height of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.height;
/**
 * The min width of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.minWidth;
/**
 * The min height of the overlay panel.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.minHeight;
/**
 * The custom class to be set on the backdrop element.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.backdropClass;
/**
 * Strategy to be used when handling scroll events while the overlay is open.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.scrollStrategy;
/**
 * Whether the overlay is open.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.open;
/**
 * Event emitted when the backdrop is clicked.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.backdropClick;
/**
 * Event emitted when the position has changed.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.positionChange;
/**
 * Event emitted when the overlay has been attached.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.attach;
/**
 * Event emitted when the overlay has been detached.
 * @type {?}
 */
ConnectedOverlayDirective.prototype.detach;
/** @type {?} */
ConnectedOverlayDirective.prototype._overlay;
/** @type {?} */
ConnectedOverlayDirective.prototype._renderer;
/** @type {?} */
ConnectedOverlayDirective.prototype._dir;
}

export class OverlayModule {static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [PortalModule, ScrollDispatchModule],
  exports: [ConnectedOverlayDirective, OverlayOrigin, ScrollDispatchModule],
  declarations: [ConnectedOverlayDirective, OverlayOrigin],
  providers: [OVERLAY_PROVIDERS],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function OverlayModule_tsickle_Closure_declarations() {
/** @type {?} */
OverlayModule.decorators;
/**
 * @nocollapse
 * @type {?}
 */
OverlayModule.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
